import { ArrowDownOutlined, ArrowUpOutlined, CopyOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Card, Form, Image, Input, InputNumber, Select, Space, Switch, Typography } from "antd";

import { FUNNEL_TEXT_TOKENS, type LandingSectionField } from "@fitness-app/data-models/landing-templates/fields";

const emptyItem = (fields: LandingSectionField[]) =>
  Object.fromEntries(
    fields.map((field) => [
      field.key,
      field.type === "boolean" ? false : field.type === "list" || field.type === "stringList" ? [] : "",
    ]),
  );

const FieldInput = ({
  field,
  value,
  onChange,
}: {
  field: LandingSectionField;
  value: unknown;
  onChange: (value: unknown) => void;
}) => {
  switch (field.type) {
    case "boolean":
      return <Switch checked={value === true} onChange={onChange} />;
    case "number":
      return (
        <InputNumber
          className="w-full"
          value={typeof value === "number" ? value : null}
          onChange={(next) => onChange(next ?? undefined)}
        />
      );
    case "select":
      return (
        <Select
          value={typeof value === "string" ? value : undefined}
          options={field.options}
          onChange={(next: string) => onChange(next)}
        />
      );
    case "stringList":
      return (
        <Input.TextArea
          rows={4}
          value={Array.isArray(value) ? value.join("\n") : ""}
          onChange={(event) => onChange(event.target.value.split("\n"))}
          onBlur={(event) =>
            onChange(
              event.target.value
                .split("\n")
                .map((line) => line.trim())
                .filter(Boolean),
            )
          }
        />
      );
    case "list": {
      const items = Array.isArray(value) ? (value as Record<string, unknown>[]) : [];
      const move = (index: number, by: number) => {
        const next = [...items];
        const [item] = next.splice(index, 1);
        next.splice(index + by, 0, item!);
        onChange(next);
      };
      return (
        <Space direction="vertical" className="w-full">
          {items.map((item, index) => (
            <Card
              key={index}
              size="small"
              title={`${field.itemLabel ?? "Element"} ${index + 1}`}
              extra={
                <Space>
                  <Button
                    size="small"
                    icon={<ArrowUpOutlined />}
                    disabled={index === 0}
                    onClick={() => move(index, -1)}
                  />
                  <Button
                    size="small"
                    icon={<ArrowDownOutlined />}
                    disabled={index === items.length - 1}
                    onClick={() => move(index, 1)}
                  />
                  <Button
                    size="small"
                    icon={<CopyOutlined />}
                    onClick={() =>
                      onChange(items.flatMap((current, i) => (i === index ? [current, { ...current }] : [current])))
                    }
                  />
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => onChange(items.filter((_, i) => i !== index))}
                  />
                </Space>
              }
            >
              <SchemaFields
                fields={field.fields}
                values={item && typeof item === "object" ? item : {}}
                onChange={(next) => onChange(items.map((current, i) => (i === index ? next : current)))}
              />
            </Card>
          ))}
          <Button icon={<PlusOutlined />} onClick={() => onChange([...items, emptyItem(field.fields)])}>
            Dodaj
          </Button>
        </Space>
      );
    }
    case "textarea":
    case "richText":
      return (
        <Input.TextArea
          rows={field.type === "richText" ? 4 : 3}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder}
          onChange={(event) => onChange(event.target.value)}
        />
      );
    default:
      return (
        <Space.Compact className="w-full">
          <Input
            value={typeof value === "string" ? value : ""}
            placeholder={field.placeholder}
            onChange={(event) => onChange(event.target.value)}
          />
          {field.type === "image" && typeof value === "string" && value ? (
            <Image src={value} width={32} height={32} className="object-cover" />
          ) : null}
          {field.type === "color" && typeof value === "string" && value ? (
            <span className="inline-block h-8 w-8 border border-gray-200" style={{ background: value }} />
          ) : null}
        </Space.Compact>
      );
  }
};

const SchemaFields = ({
  fields,
  values,
  onChange,
}: {
  fields: LandingSectionField[];
  values: Record<string, unknown>;
  onChange: (values: Record<string, unknown>) => void;
}) => (
  <>
    {fields.map((field) => (
      <Form.Item key={field.key} label={field.label} help={field.help} className="mb-3">
        <FieldInput
          field={field}
          value={values[field.key]}
          onChange={(value) => onChange({ ...values, [field.key]: value })}
        />
      </Form.Item>
    ))}
  </>
);

/**
 * Form of a funnel template section, generated from its `LandingSectionDefinition.fields`
 * (data-models `landing-templates`); the visual editor in ecommerce renders the same schema.
 */
export const SchemaPropsEditor = ({
  fields,
  props,
  onPropsChange,
}: {
  fields: LandingSectionField[];
  props: Record<string, unknown>;
  onPropsChange: (props: Record<string, unknown>) => void;
}) => (
  <>
    <SchemaFields fields={fields} values={props} onChange={onPropsChange} />
    <Typography.Text type="secondary" className="text-xs">
      W tekstach działają wstawki z danych funnelu: {FUNNEL_TEXT_TOKENS.map((token) => token.token).join(", ")}.
      Wyróżnienia: &lt;strong&gt;, &lt;em&gt;, &lt;s&gt;, &lt;mark&gt;, &lt;br /&gt;.
    </Typography.Text>
  </>
);
